import { createFileRoute, Outlet, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../components/ui/Skeleton';
import { ErrorState } from '../components/feedback/ErrorState';
import { useAuth } from '../hooks/useAuth.js';
import { AccountTabs } from '../components/features/sns-content-generator/AccountTabs/index.js';
import { DEFAULT_SNS_CONTENT_GENERATOR_ACCOUNT_ID } from '../components/features/sns-content-generator/accounts.js';

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business SNSコンテンツ生成レイアウトルート。子ルート（生成・編集/確認）のOutletラッパー。
 * サイドバーのリンク出し分けだけでは URL 直打ちで到達できてしまうため、ここでも
 * user.canUseSnsContentGenerator を確認し、権限が無ければ案内表示にフォールバックする。
 * マルチアカウント対応のアカウント切り替えタブもここに置く（配下全体で共通のため）
 * @graph-connects useAuth [calls] 現在のユーザー情報取得（canUseSnsContentGenerator チェック）
 * @graph-connects AccountTabs [calls] アカウント切り替えタブ描画
 */
export const Route = createFileRoute('/sns-content-generator')({
  component: SnsContentGeneratorLayout,
});

/** @graph-connects none */
export function SnsContentGeneratorLayout() {
  const { t } = useTranslation();
  const { user, isLoading: isAuthLoading } = useAuth();
  // 子ルートが `/sns-content-generator/$accountId` にマッチしているときだけ accountId が取れる。
  // `/sns-content-generator`（リダイレクトのみ）や `/sns-content-generator/tiktok/callback`
  // （TikTok連携ポップアップの独立画面）にいる間はタブを出さない。
  const accountId = useParams({ strict: false }).accountId;

  if (isAuthLoading) {
    return <Skeleton className="h-32" />;
  }
  if (user && !user.canUseSnsContentGenerator) {
    return (
      <ErrorState
        title={t('common.accessDeniedTitle')}
        message={t('snsContentGenerator.accessDenied')}
      />
    );
  }

  return (
    <div className="space-y-6">
      {accountId !== undefined && (
        <AccountTabs activeAccountId={accountId ?? DEFAULT_SNS_CONTENT_GENERATOR_ACCOUNT_ID} />
      )}
      <Outlet />
    </div>
  );
}
